import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { stripEnPrefix, toLangPath, hasEnPrefix } from "../../utilities/helpers";
import { SITE_URL, SITE_NAME, SITE_LEGAL_NAME, DEFAULT_IMAGE, DEFAULT_TITLE, DEFAULT_DESCRIPTION, CONTACT } from "../../config/seo";

const MAX_DESCRIPTION = 160;

// Search engines treat /en and /en/ as two URLs, so every URL we emit is
// normalised the same way: no trailing slash except on the root.
const normalizePath = (pathname = "/") => {
  const clean = (pathname || "/").replace(/[#?].*$/, "");
  if (clean === "" || clean === "/") return "/";
  return clean.replace(/\/+$/, "") || "/";
};

const trimDescription = (value = "") => {
  const text = value.toString().replace(/\s+/g, " ").trim();
  if (text.length <= MAX_DESCRIPTION) return text;
  const cut = text.slice(0, MAX_DESCRIPTION);
  const lastSpace = cut.lastIndexOf(" ");
  return `${(lastSpace > 80 ? cut.slice(0, lastSpace) : cut).replace(/[,;:.\s]+$/, "")}…`;
};

export default function SEO({
  title,
  description,
  name,
  type = "website",
  image,
  canonical,
  jsonLd = [],
  noindex = false,
  breadcrumbs,
  publishedTime,
  modifiedTime,
  alternates,
}) {
  const location = useLocation();
  const pathname = normalizePath(location?.pathname || window.location.pathname);
  const lang = hasEnPrefix(pathname) ? "en" : "bg";

  // Prefixing /en onto the current path only produces the right alternate when
  // both languages share the same path. Article URLs embed a localised slug, so
  // those callers pass the real pair via `alternates`.
  const bgPath = normalizePath(alternates?.bg || stripEnPrefix(pathname));
  const enPath = normalizePath(alternates?.en || toLangPath(normalizePath(stripEnPrefix(pathname)), "en"));

  const canonicalUrl = canonical || `${SITE_URL}${pathname}`;
  const bgUrl = `${SITE_URL}${bgPath}`;
  const enUrl = `${SITE_URL}${enPath}`;

  const pageTitle = title || DEFAULT_TITLE[lang];
  const pageDescription = trimDescription(description || DEFAULT_DESCRIPTION[lang]);
  const pageImage = image || DEFAULT_IMAGE;
  // og:type only accepts a handful of values; twitter:card is a separate vocabulary.
  const ogType = type === "article" ? "article" : "website";
  // The fallback logo is square, which the large card crops badly — only claim
  // the wide card when the page supplies its own (landscape) image.
  const twitterCard = image ? "summary_large_image" : "summary";

  const siteOrg = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: SITE_NAME,
    legalName: SITE_LEGAL_NAME,
    url: `${SITE_URL}/`,
    logo: { "@type": "ImageObject", url: DEFAULT_IMAGE },
    email: CONTACT.email,
    telephone: CONTACT.phone,
    address: {
      "@type": "PostalAddress",
      streetAddress: lang === "en" ? CONTACT.streetEn : CONTACT.street,
      addressLocality: lang === "en" ? CONTACT.cityEn : CONTACT.city,
      addressCountry: CONTACT.country,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: CONTACT.phone,
      email: CONTACT.email,
      contactType: "customer service",
      areaServed: "BG",
      availableLanguage: ["bg", "en"],
    },
  };

  const siteWeb = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: SITE_NAME,
    url: `${SITE_URL}/`,
    inLanguage: lang,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };

  const breadcrumbBlock =
    breadcrumbs && breadcrumbs.length
      ? {
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          itemListElement: breadcrumbs.map((crumb, i) => ({
            "@type": "ListItem",
            position: i + 1,
            name: crumb.name,
            item: crumb.url?.startsWith("http") ? crumb.url : `${SITE_URL}${normalizePath(crumb.url || "/")}`,
          })),
        }
      : null;

  const jsonLdBlocks = [siteOrg, siteWeb, ...(breadcrumbBlock ? [breadcrumbBlock] : []), ...jsonLd].filter(Boolean);

  // defer={false} applies the tags during commit instead of inside a
  // requestAnimationFrame callback. rAF is throttled or never fires while the
  // tab is backgrounded, which left the head with only the index.html defaults.
  return (
    <Helmet htmlAttributes={{ lang }} defer={false}>
      {/* Standard metadata tags */}
      <title>{pageTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="robots" content={noindex ? "noindex, follow" : "index, follow, max-image-preview:large, max-snippet:-1, max-video-preview:-1"} />

      {/* A noindex URL (form step, 404) gets neither a canonical nor hreflang:
          both would assert it is a legitimate, indexable page. */}
      {!noindex && <link rel="canonical" href={canonicalUrl} />}
      {!noindex && <link rel="alternate" hrefLang="bg" href={bgUrl} />}
      {!noindex && <link rel="alternate" hrefLang="en" href={enUrl} />}
      {!noindex && <link rel="alternate" hrefLang="x-default" href={bgUrl} />}

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content={lang === "en" ? "en_US" : "bg_BG"} />
      <meta property="og:locale:alternate" content={lang === "en" ? "bg_BG" : "en_US"} />
      <meta property="og:type" content={ogType} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={pageImage} />
      <meta property="og:image:alt" content={name || pageTitle} />
      {ogType === "article" && publishedTime ? <meta property="article:published_time" content={publishedTime} /> : null}
      {ogType === "article" && modifiedTime ? <meta property="article:modified_time" content={modifiedTime} /> : null}

      {/* Twitter / X */}
      <meta name="twitter:card" content={twitterCard} />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={pageImage} />
      <meta name="twitter:image:alt" content={name || pageTitle} />

      {/* JSON-LD structured data */}
      {jsonLdBlocks.map((block, idx) => (
        <script key={`jsonld-${idx}`} type="application/ld+json">
          {JSON.stringify(block)}
        </script>
      ))}
    </Helmet>
  );
}
